// Offline meal queue: food-log POSTs that fail without network are stored
// per user in localStorage and re-sent automatically once we're back online.
import { api } from "@/lib/http";
import { refreshWidgetFromServer } from "@/lib/widgetSync";

const KEY = (uid) => `solano_offline_meals_${uid || "anon"}`;
const EVT = "solano:offline-meals-changed";
let _uid = null;
let _notify = null;
let _flushing = false;

export const setOfflineMealsUser = (uid, notify) => { _uid = uid || null; _notify = notify || null; };

export function getPendingMeals(date) {
  try {
    const list = JSON.parse(localStorage.getItem(KEY(_uid)) || "[]");
    return date ? list.filter((m) => m.payload?.date === date) : list;
  } catch {
    return [];
  }
}

const save = (list) => {
  try { localStorage.setItem(KEY(_uid), JSON.stringify(list)); } catch { /* quota */ }
  window.dispatchEvent(new CustomEvent(EVT));
};

export const onPendingMealsChange = (fn) => {
  window.addEventListener(EVT, fn);
  return () => window.removeEventListener(EVT, fn);
};

export const isNetworkError = (err) => !err?.response;

const newRef = () => `off_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function enqueueMeal(payload) {
  const ref = newRef();
  const item = { ref, queued_at: Date.now(), payload: { ...payload, client_ref: ref } };
  const list = getPendingMeals();
  list.push(item);
  try {
    localStorage.setItem(KEY(_uid), JSON.stringify(list));
  } catch {
    // Photo too big for storage → keep the entry, drop the photo.
    item.payload.photo_base64 = null;
    try { localStorage.setItem(KEY(_uid), JSON.stringify(list)); } catch { /* give up */ }
  }
  window.dispatchEvent(new CustomEvent(EVT));
  return item;
}

export function removePendingMeal(ref) {
  save(getPendingMeals().filter((m) => m.ref !== ref));
}

const offlineResponse = (item) => {
  try { _notify?.(); } catch { /* noop */ }
  return { data: { ...item.payload, log_id: `offline_${item.ref}`, pending_sync: true }, offline: true };
};

// Drop-in replacement for api.post("/client/food-log", payload).
// Resolves with { data, offline: true } when the entry had to be queued.
export async function postFoodLog(payload) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return offlineResponse(enqueueMeal(payload));
  }
  try {
    return await api.post("/client/food-log", payload);
  } catch (err) {
    if (isNetworkError(err)) return offlineResponse(enqueueMeal(payload));
    throw err;
  }
}

// Re-send queued entries in order. Stops at the first network failure.
// Returns { sent, failed, remaining }.
export async function flushOfflineMeals() {
  if (_flushing) return { sent: 0, failed: 0, remaining: getPendingMeals().length };
  _flushing = true;
  let sent = 0, failed = 0;
  try {
    for (const item of getPendingMeals()) {
      try {
        await api.post("/client/food-log", item.payload);
        sent += 1;
        removePendingMeal(item.ref);
      } catch (err) {
        if (isNetworkError(err)) break;
        // Server rejected the entry (validation/auth) → don't retry forever.
        if (err?.response?.status !== 401) { failed += 1; removePendingMeal(item.ref); }
        else break;
      }
    }
  } finally {
    _flushing = false;
  }
  if (sent > 0) {
    try { window.dispatchEvent(new CustomEvent("solano:log-updated")); } catch { /* noop */ }
    refreshWidgetFromServer();
  }
  return { sent, failed, remaining: getPendingMeals().length };
}
